import { useQuery } from '@tanstack/react-query';
import { useAppDispatch, useAppSelector } from '../../../hooks/useAppStore';
import { getAuthenticateUser } from '../../../store/authenticate/selectors';
import { inventoryActions } from '../../../store/inventory/slice';
import { getCurrentWorker, getInventory, getSelectedLocationId } from '../../../store/inventory/selector';
import { TPageInfo } from '../../../types';
import { BASE_URL } from '../../../../config';
import { CONTROLLERS } from '../../../utils/enums';
import { useInventory } from '../Shared/useInventory';
import { getIdToken } from '../../../utils/helpers';

export const useFetchRemainingProducts = (pageInfoData: TPageInfo | undefined) => {
  const dispatch = useAppDispatch();
  const { personnel } = useAppSelector(getAuthenticateUser);
  const selectedLocationId = useAppSelector(getSelectedLocationId);
  const currentWorker = useAppSelector(getCurrentWorker);
  const inventoryItems = useAppSelector(getInventory);
   const { extractFilterParams } = useInventory();
  const controllerName = CONTROLLERS.INVENTORY;

  return useQuery(
    ['remainingProducts', pageInfoData, selectedLocationId],
    async () => {
      if (!pageInfoData) {
        throw new Error('pageInfoData is required to fetch remaining products.');
      }

      if (selectedLocationId! <= 1 || selectedLocationId === null) {
        const tempPageInfo: TPageInfo = {
          hasNextPage: false,
          endCursor: null,
        };
        return tempPageInfo;
      }

      if (pageInfoData === undefined) {
        return inventoryItems[selectedLocationId].pageInfo;
      }

      if (!pageInfoData?.hasNextPage) {
        return pageInfoData;
      }

      let workerCompleted = false;

      const selectedLocationWorker = inventoryItems[selectedLocationId].currentWorker;
      if (selectedLocationWorker !== currentWorker) {
        if (currentWorker && typeof currentWorker.terminate === 'function') {
          currentWorker.terminate();
        }
      }
      const worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });

      const fetchPromise = new Promise<void>((resolve, reject) => {
        worker.onmessage = (event) => {
          if (event.data.error) {
            reject(event.data.error);
          } else if (event.data.complete) {
            workerCompleted = true;
            resolve();
          } else {
            const filterParams = extractFilterParams(event.data.productVariants);

            dispatch(inventoryActions.addVariantsToInventory({ locationId: selectedLocationId, variants: event.data.productVariants }));

            dispatch(inventoryActions.setCategories(filterParams.categories));
            dispatch(inventoryActions.setProductTypes(filterParams.productTypes));
            dispatch(inventoryActions.setTags(filterParams.tags));
            dispatch(inventoryActions.setVendors(filterParams.vendors));

            const pageInfo : TPageInfo = {
              hasNextPage: event.data.hasNextPage,
              endCursor: event.data.cursor,
            }
            dispatch(inventoryActions.addProductsToLocation({
              locationId: selectedLocationId!,
              products: [],
              pageInfo: pageInfo,
            }));
          }
        };
      });

      worker.postMessage({
        baseUrl: `${BASE_URL}/${controllerName}/get`,
        shopName: personnel.shopName,
        locationId: selectedLocationId,
        cursor: pageInfoData?.endCursor,
        hasNextPage: pageInfoData?.hasNextPage,
        token: getIdToken(''),
      });

      dispatch(inventoryActions.setWorker({ locationId: selectedLocationId!, worker }));
      dispatch(inventoryActions.setCurrentWorker(worker));
      try {
        await fetchPromise;

        if (workerCompleted) {
          dispatch(inventoryActions.setProductsIsLoading({ locationId: selectedLocationId!, isLoading: false }));
        }
      } catch (err) {
        console.error('Error on worker: ', err);
      }
      return inventoryItems[selectedLocationId!].pageInfo;
    },
    {
      enabled: !!pageInfoData && inventoryItems[selectedLocationId!]?.pageInfo?.hasNextPage,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      refetchOnReconnect: false,
      staleTime: 0,
      cacheTime: 0,
    },
  );
};
